export const CALLING_LIST_RANKING_MIN = 1
export const CALLING_LIST_RANKING_MAX = 9999
export const CALLING_LIST_RANKING_EMPTY_DISPLAY = '-'
export const CALLING_LIST_RANKING_VALIDATION_MESSAGE = `Ranking must be a whole number from ${CALLING_LIST_RANKING_MIN} to ${CALLING_LIST_RANKING_MAX}.`
export const CALLING_LIST_RANKING_SAVE_ERROR_MESSAGE = 'Failed to save calling list ranking'

export type CallingListRankingInput = unknown
export type CallingListRanking = number | null

const isBlankRanking = (value: CallingListRankingInput) =>
  value === undefined || value === null || (typeof value === 'string' && value.trim() === '')

export const normalizeCallingListRanking = (value: CallingListRankingInput): CallingListRanking => {
  if (isBlankRanking(value)) {
    return null
  }

  if (typeof value !== 'number' && typeof value !== 'string') {
    throw new Error(CALLING_LIST_RANKING_VALIDATION_MESSAGE)
  }

  if (typeof value === 'string' && !/^\d+$/.test(value.trim())) {
    throw new Error(CALLING_LIST_RANKING_VALIDATION_MESSAGE)
  }

  const numericValue = typeof value === 'string' ? Number(value.trim()) : value

  if (!Number.isInteger(numericValue)) {
    throw new Error(CALLING_LIST_RANKING_VALIDATION_MESSAGE)
  }

  if (numericValue === 0) {
    return null
  }

  if (numericValue < CALLING_LIST_RANKING_MIN || numericValue > CALLING_LIST_RANKING_MAX) {
    throw new Error(CALLING_LIST_RANKING_VALIDATION_MESSAGE)
  }

  return numericValue
}

export const getRankingInputValue = (ranking: CallingListRankingInput): string =>
  ranking === null || ranking === undefined ? '' : `${ranking}`

export const normalizeRawCallingListRanking = (
  value: CallingListRankingInput
): CallingListRanking => {
  try {
    return normalizeCallingListRanking(value)
  } catch {
    return null
  }
}

export const formatCallingListRanking = (value: CallingListRankingInput): string => {
  const ranking = normalizeCallingListRanking(value)

  return ranking === null ? CALLING_LIST_RANKING_EMPTY_DISPLAY : ranking.toString()
}

// Formats free-form keyboard input into a valid ranking draft: strips
// non-digits, treats 0 as "no ranking" (empty) and caps at the maximum.
export const formatRankingInputValue = (value: string): string => {
  const digits = value.replace(/\D/g, '')

  if (!digits) {
    return ''
  }

  const numericValue = Number(digits)

  if (numericValue === 0) {
    return ''
  }

  return Math.min(numericValue, CALLING_LIST_RANKING_MAX).toString()
}

// Returns the validation message for an invalid ranking draft, '' when valid.
export const validateRankingInput = (value: CallingListRankingInput): string => {
  try {
    normalizeCallingListRanking(value)
    return ''
  } catch {
    return CALLING_LIST_RANKING_VALIDATION_MESSAGE
  }
}

// Like formatCallingListRanking, but falls back to the empty display instead
// of throwing for values that fail ranking validation.
export const formatRankingDisplay = (value: CallingListRankingInput): string => {
  try {
    return formatCallingListRanking(value)
  } catch {
    return CALLING_LIST_RANKING_EMPTY_DISPLAY
  }
}

export const compareCallingListRankings = (
  left: CallingListRankingInput,
  right: CallingListRankingInput
): number => {
  const leftRanking = normalizeCallingListRanking(left)
  const rightRanking = normalizeCallingListRanking(right)

  if (leftRanking === null && rightRanking === null) {
    return 0
  }

  if (leftRanking === null) {
    return 1
  }

  if (rightRanking === null) {
    return -1
  }

  return leftRanking - rightRanking
}
